import { combineLatest, EMPTY, Subscription } from 'rxjs';

import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { CAPITAL_OPERATIONS } from '@models/puppet.class';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
} from 'rxjs/operators';
import {
  AppState,
  OPERATION_CLEAR_TEXT,
  OPERATION_RESET,
  ORDERS_LIST,
  SOLACE_SUBSCRIPTIONS,
  USER_ROLES,
} from 'src/app';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { PositionData } from '@models/positionData.class';
import { LevelViewModel } from '@models/level-two.class';
import { SearchBoxIssueItem } from '@models/search-box-issue-item';
import { BootstrapService } from 'src/core/services/bootstrap.service';
import { MarketService } from 'src/core/services/market.service';
import { PuppetService } from 'src/core/services/puppet.service';
import {
  getMarketName,
  MarketHecho,
  MarketType,
  SharedSolaceMainService,
  SharedSolaceStreamService,
} from 'src/solace';

@Component({
  selector: 'hb-trade-capitals',
  templateUrl: 'tradeCapitals.component.html',
  styleUrls: ['./tradeCapitals.component.scss'],
})
export class TradeCapitalsComponent
  implements OnChanges, OnInit, OnDestroy, AfterContentInit
{
  @Input() operationStatus: boolean;
  @Input() inheritedOrder: any;
  @Input() isGeolocationEnabled: boolean;
  @Input() isPositionMocked: boolean;
  @Input() shouldStartLoaderOnOperationAttempt: boolean;
  @Input() shouldNotifyGeolocationOnOperationAttempt: boolean;

  @Output() changeOperationStatus = new EventEmitter<boolean>();
  @Output() changeOrderType = new EventEmitter<any>();

  // User
  /**
   * Forces operative mode (el patron)
   */
  userInfo: any = {
    hasLevel2: false,
    isReadAndWrite: true,
  };

  selectedOrder: any = {};
  selectedPrice: number;
  hasShortSale = false;
  replaceTitles = 0;
  ordenType: any = ORDERS_LIST;

  selectedOrderClass = 'limitada';

  // Position
  userHasIssue = false;
  position: any = {
    shares: 0,
    plusMinus: 0,
    ctoProm: 0,
    var: 0,
  };

  public isUsaContract: boolean;
  public readonly capitalOperations = CAPITAL_OPERATIONS;
  public usaOperation: CAPITAL_OPERATIONS = CAPITAL_OPERATIONS.buy;
  public usaCanSell = false;

  public operations = true;

  // Solace
  solaceInfo: any = {};

  // Level 2
  level2: LevelViewModel[] = [];

  scrolledSet = false;
  scrollableHeight: any = 'initial';

  public userGlobalSubscription: Subscription;
  public userRoleSubscription: Subscription;
  public isUserGlobal = false;
  public isInternalUser = false;
  public userRole: number;
  public NOT_SHOW_SHORTSALE = false;
  public showSearchTooltip = false;

  // Solace
  public currentContract: any;
  public hasShortSellContract: any;
  public currentIssue: SearchBoxIssueItem;

  public lockSelectCombo = false;
  public elPatronData: any;
  public isDiscretional: boolean;

  // Solace
  private consumerId = 'tradebar-capitals';
  private consumerIssueDetailId = 'issueDetail';
  private consumerMonitorId = 'monitor';
  private consumerSearchId = 'search';

  private subscription: Subscription;

  private subscriptions = new Subscription();

  private positionSymbolSubscription: Subscription;
  private symbolSolacePosturasSubscription: Subscription;

  private selectedTabType = 0;
  private emptyState = true;

  private subscribedSymbol = false;

  private positionValueTypes: any = {
    NACIONAL: 1,
    SIC: 0,
    GARANTIAS_EN_PRESTAMO: 8,
    TRADING_USA: 100,
  };

  constructor(
    private store: Store<AppState>,
    private bootstrapService: BootstrapService,
    private marketService: MarketService,
    private sharedSolaceStreamService: SharedSolaceStreamService,
    private sharedSolaceMainService: SharedSolaceMainService,
    private cdref: ChangeDetectorRef,
    private puppetService: PuppetService,
  ) {}

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const { operationStatus } = changes;
    if (operationStatus && operationStatus.currentValue !== undefined) {
      this.emptyState = !operationStatus.currentValue;
    }
  }

  changeType(type: number) {
    this.selectedTabType = type;
  }

  toggleEmptyState(state: boolean) {
    this.emptyState = state;
    this.changeOperationStatus.emit(state);
  }

  doHardReset() {
    if (this.subscribedSymbol) {
      this.unsubscribeSolace();
      this.subscribedSymbol = false;
    }
    this.selectedPrice = 0;
    this.currentIssue = undefined;
    this.level2 = [];
    this.replaceTitles = 0;

    this.userHasIssue = false;
    this.position = {
      shares: 0,
      plusMinus: 0,
      ctoProm: 0,
      var: 0,
      costAmount: 0,
      closePrice: 0,
      valueType: 0,
    };

    this.puppetService.updatePosition(this.position);
    this.usaCanSell = false;
    this.usaOperation = CAPITAL_OPERATIONS.buy;

    this.store.dispatch({
      type: OPERATION_RESET,
      payload: { hardReset: false },
    });
  }

  swapOrdertype() {
    this.lockSelectCombo = true;
    this.replaceTitles = 0;
    this.selectedOrder = { id: -1, name: 'none' };
    setTimeout(() => {
      this.lockSelectCombo = false;
      this.selectedPrice = 0;
      this.setOrderType(this.inheritedOrder);
    }, 500);
  }

  ngAfterContentInit() {
    this.doScrollArragenment();
    this.selectedOrder = this.inheritedOrder;
    this.cdref.detectChanges();
  }

  scanForElPatronData() {
    this.subscriptions.add(
      this.store
        .select('elPatronData')
        .pipe(
          map((value: any) => value.toJS()),
          filter((value: any) => value.URL_SETTINGS !== undefined),
          take(1),
        )
        .subscribe((value: any) => {
          this.elPatronData = value;
        }),
    );
  }

  getDiscretionalStatus(discretionalParameter: any) {
    this.isDiscretional = discretionalParameter;
    this.puppetService.updateIsDiscretional(discretionalParameter);
  }

  ngOnInit() {
    this.bootstrapService.initSession().then(() => {
      this.subscription = this.store
        .select('loggedInUser')
        .pipe(
          map((value: any) => value.toJS()),
          map((value: any) => {
            return {
              hasLevel2: value.hasLevel2,
              isReadAndWrite: value.isReadAndWrite,
              mfaType: value.mfaType,
              readToken: value.sessionToken,
            };
          }),
        )
        .subscribe((userInfo: any) => {
          this.userInfo.hasLevel2 = userInfo.hasLevel2;
          this.userInfo.isReadAndWrite = userInfo.isReadAndWrite;
          this.userInfo.mfaType = userInfo.mfaType;
          this.userInfo.readToken = userInfo.readToken;
        });

      const currentContractSubscription = this.store
        .select('currentContract')
        .pipe(map((values: any) => values.toJS()))
        .subscribe((value: any) => {
          if (
            this.currentContract &&
            this.currentContract !== value.contractID
          ) {
            this.changeOrderType.emit(this.ordenType[0]);
            this.swapOrdertype();
          }
          this.currentContract = value.contractID;
          this.hasShortSellContract = value.tradeShortSell;
          this.getDiscretionalStatus(value.isDiscretional);
        });

      const currentSymbolSubscription = this.store
        .select('currentSymbol')
        .pipe(
          map((value: any) => (value ? value.toJS() : {})),
          filter((value: any) => value.issueID !== undefined),
        )
        .subscribe((issue: any) => {
          this.currentIssue = issue;

          if (this.positionSymbolSubscription) {
            this.positionSymbolSubscription.unsubscribe();
          }
          this.solaceSubscriptions();

          this.toggleEmptyState(false);
          this.userHasIssue = false;
          this.position.shares = 0;
          this.position.plusMinus = 0;
          this.position.ctoProm = 0;
          this.position.var = 0;
          this.position.costAmount = 0;
          this.position.closePrice = 0;
          this.position.valueType = 0;

          const previousUsaOperation = this.usaOperation;
          this.usaOperation = CAPITAL_OPERATIONS.buy;
          this.usaCanSell = false;

          this.positionSymbolSubscription = this.store
            .select('position')
            .pipe(
              map((values: any) => values.toJS()),
              distinctUntilChanged(),
            )
            .subscribe((positions: any[]) => {
              if (positions && this.currentIssue && this.currentIssue.issueID) {
                const positionTypes: any =
                  Object.getOwnPropertyNames(positions);
                // tslint:disable-next-line prefer-for-of
                for (let i = 0; i < positionTypes.length; i++) {
                  if (
                    positions[positionTypes[i]][0] !== undefined &&
                    (positions[positionTypes[i]][0].positionValueType ===
                      this.positionValueTypes.SIC ||
                      positions[positionTypes[i]][0].positionValueType ===
                        this.positionValueTypes.NACIONAL ||
                      positions[positionTypes[i]][0].positionValueType ===
                        this.positionValueTypes.GARANTIAS_EN_PRESTAMO ||
                      positions[positionTypes[i]][0].positionValueType ===
                        this.positionValueTypes.TRADING_USA)
                  ) {
                    // TODO: Prefer .find over .filter
                    const positionByIssue = positions[positionTypes[i]].filter(
                      (values: any) => {
                        return (
                          values.issueId ===
                          this.currentIssue.issueID.replace(/Ñ/g, '&')
                        );
                      },
                    );
                    this.userHasIssue = false;
                    if (positionByIssue.length > 0) {
                      this.userHasIssue = true;
                      this.usaCanSell = true;
                      this.usaOperation = previousUsaOperation;

                      const costAmount = parseFloat(
                        positionByIssue[0].averageCost.toFixed(4),
                      );
                      const priceForMarketValue =
                        positionByIssue[0].weightedAveragePrice > 0
                          ? positionByIssue[0].weightedAveragePrice
                          : positionByIssue[0].lastPrice;

                      this.position.marketValue =
                        positionByIssue[0].marketValue;
                      this.position.shares = positionByIssue[0].quantity;
                      this.position.plusMinus =
                        positionByIssue[0].quantity * priceForMarketValue -
                        costAmount;
                      this.position.ctoProm = positionByIssue[0].averagePrice;
                      this.position.var =
                        positionByIssue[0].historicalVariationPercentage;
                      this.position.costAmount = costAmount;
                      this.position.closePrice = positionByIssue[0].closePrice;
                      this.position.valueType =
                        positionByIssue[0].positionValueType;

                      this.puppetService.updatePosition(this.position);

                      break;
                    }
                  }
                }
              }
            });

          this.getLevel(issue);
          this.getSolaceInfo(issue);
        });

      const resetSubscription = this.store
        .select('operation')
        .pipe(
          map((value: any) => value.toJS()),
          distinctUntilChanged(),
        )
        .subscribe((value: any) => {
          if (value.reset) {
            this.swapOrdertype();
            this.store.dispatch({
              type: OPERATION_RESET,
              payload: { reset: false },
            });
          }
          if (value.price) {
            this.selectedPrice = value.price;
          }
          if (value.hardReset) {
            this.doHardReset();
          }
          if (value.clearText) {
            this.store.dispatch({
              type: OPERATION_CLEAR_TEXT,
              payload: { clearText: false },
            });
          }
        });

      // Fill the CapitalTrade for a Replace Order
      const cancelReplaceOrder = this.store
        .select('triggerCancelReplace')
        .pipe(
          map((value: any) => value.toJS()),
          distinctUntilChanged(),
        )
        .subscribe((value: any) => {
          const replace = value.isCancelReplace;
          const orderPrice = value.orderPrice;
          let orderType: any[] = [];
          orderType = this.ordenType.filter((o: any) => {
            return o.id === value.orderTypeId;
          });
          this.replaceTitles = 0;

          if (orderType.length > 0) {
            this.fillOrderType();
            this.setOrderType(orderType[0]);
          }

          if (replace === true) {
            this.selectedPrice = orderPrice;
            this.replaceTitles = value.orderReplaceTitles;
          }
        });

      this.subscription.add(currentContractSubscription);
      this.subscription.add(currentSymbolSubscription);
      this.subscription.add(resetSubscription);
      this.subscription.add(cancelReplaceOrder);
    });

    this.store
      .select(selectIsUsa)
      .subscribe((isUsa) => (this.isUsaContract = isUsa));

    this.scanForUserRole();
    this.scanForUserTypeContract();
    this.scanForElPatronData();
  }

  ngOnDestroy() {
    if (this.currentIssue !== undefined) {
      const marketName: string =
        this.currentIssue.instrumentType === MarketType.BMV
          ? 'marketBMV'
          : 'marketSIC';
      this.removeConsumerSubscription(this.currentIssue.issueID, marketName);
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.subscribedSymbol) {
      this.unsubscribeSolace();
      this.subscribedSymbol = false;
    }

    if (this.positionSymbolSubscription) {
      this.positionSymbolSubscription.unsubscribe();
    }

    if (this.userRoleSubscription) {
      this.userRoleSubscription.unsubscribe();
    }

    if (this.userGlobalSubscription) {
      this.userGlobalSubscription.unsubscribe();
    }

    this.subscriptions.unsubscribe();

    this.changeOrderType.emit(this.selectedOrder);
  }

  @HostListener('window:scroll', ['$event']) onWindowScroll($event: any) {
    this.doScrollArragenment();
  }

  public get missingInformationText(): string {
    return this.isPositionMocked && this.currentIssue ? 'No disponible' : '--';
  }

  public get shouldDisplayIssueData(): boolean {
    return this.userHasIssue && !this.isPositionMocked;
  }

  public setCopiedPrice($event: any) {
    this.selectedPrice = $event;
  }

  public setOrderType(order: any) {
    if (order.id === '1') {
      this.selectedOrderClass = 'limitada';
    } else if (order.id === '2') {
      this.selectedOrderClass = 'enlinea';
    } else {
      this.selectedOrderClass = 'default';
    }

    this.selectedOrder = order;
  }

  public setUsaOperation(operation: CAPITAL_OPERATIONS) {
    if (operation === CAPITAL_OPERATIONS.sell && !this.usaCanSell) {
      return;
    }
    this.usaOperation = operation;
  }

  /**
   * It gets data from Level One or Level Two depending on userInfo.hasLevel2
   */
  private getLevel(issue: any) {
    if (this.isUsaContract) {
      return;
    }

    const marketName: string =
      issue.instrumentType === MarketType.BMV ? 'BMV' : 'SIC';
    const level$ = !this.userInfo.hasLevel2
      ? this.marketService.getLevelOne(issue.issueID, marketName)
      : this.marketService.getLevelTwo(issue.issueID, marketName);

    this.subscriptions.add(
      level$
        .pipe(catchError(() => EMPTY))
        .subscribe((result: LevelViewModel[]) => (this.level2 = result)),
    );
  }

  private doScrollArragenment() {
    const headerHeight = 373;
    const MIN_HEIGHT = 200;

    if (window.scrollY > 120) {
      if (this.scrolledSet) {
        return;
      }
      this.scrolledSet = true;

      this.scrollableHeight =
        window.innerHeight - headerHeight > MIN_HEIGHT
          ? `${window.innerHeight - headerHeight}px`
          : `${MIN_HEIGHT}px`;
    } else {
      this.scrolledSet = false;
      this.scrollableHeight = 'initial';
    }
  }

  private fillOrderType(): void {
    this.ordenType = ORDERS_LIST.map(({ id, name }) => ({ id, name }));
  }

  private getSolaceInfo(issue: any): void {
    const marketName = getMarketName(issue.instrumentType);
    this.createSymbolSolaceObservable(issue.issueID, marketName);
  }

  private createSymbolSolaceObservable(
    symbol: string,
    marketName: string,
  ): void {
    const positionData = new PositionData();
    this.removeConsumerSubscription(symbol, marketName);

    const portfolio$ = this.store.select('portfolio').pipe(
      map((value: any) => value.get('positionUseSolaceConnection')),
      distinctUntilChanged(),
    );

    const market$: any = this.sharedSolaceStreamService
      .getObservableSymbol(marketName, symbol, this.consumerId)
      .pipe(
        filter((symbolShadowed: any) => symbolShadowed !== undefined),
        map((symbolShadowed: any) => [
          symbolShadowed.posturas.body,
          symbolShadowed.hechos,
        ]),
        distinctUntilChanged((a: any, b: any) => {
          const [posturas0] = a;
          const [posturas1] = b;
          return isEqual(posturas0, posturas1);
        }),
      );

    this.symbolSolacePosturasSubscription = combineLatest(
      portfolio$,
      market$,
      (portfolio, market) => {
        return {
          positionUseSolaceConnection: portfolio,
          market,
        };
      },
    ).subscribe(
      ({
        positionUseSolaceConnection,
        market,
      }: {
        positionUseSolaceConnection: boolean;
        market: [any[], MarketHecho];
      }) => {
        const [posturas, hechos] = market;

        this.level2 = posturas.map((postura) =>
          LevelViewModel.fromWebsocket(postura),
        );

        if (this.userHasIssue && positionUseSolaceConnection) {
          let solacePrice = 0;
          let priceForMarketValue = 0;
          solacePrice = hechos.body.lastPrice;
          this.position.solaceMarketPrice = solacePrice;
          this.position.ppp =
            hechos.body.bmvWeightedAvgPrice > 0
              ? hechos.body.bmvWeightedAvgPrice
              : hechos.body.weightedAvgPrice;
          priceForMarketValue =
            hechos.body.bmvWeightedAvgPrice > 0
              ? hechos.body.bmvWeightedAvgPrice
              : solacePrice;
          this.position.marketValue =
            priceForMarketValue * this.position.shares;
          this.position.plusMinus =
            this.position.shares * priceForMarketValue -
            this.position.costAmount;
          this.position.var = positionData.calculateHistoricalVariation(
            this.position.marketValue,
            this.position.costAmount,
            this.position.valueType,
          );

          this.puppetService.updatePosition(this.position);
        }
      },
    );
  }

  private removeConsumerSubscription(symbol: string, marketName: string): void {
    this.sharedSolaceStreamService.removeConsumerFromObservable(
      symbol,
      marketName,
      this.consumerId,
    );

    if (this.symbolSolacePosturasSubscription) {
      this.symbolSolacePosturasSubscription.unsubscribe();
    }
  }

  private unsubscribeSolace() {
    let PROFUNDIDAD = '';

    if (this.userInfo.hasLevel2) {
      PROFUNDIDAD =
        this.currentIssue.instrumentType === MarketType.BMV
          ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_BMV
          : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_SIC;
    } else {
      PROFUNDIDAD =
        this.currentIssue.instrumentType === MarketType.BMV
          ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_BMV
          : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_SIC;
    }

    const marketName: string = getMarketName(this.currentIssue.instrumentType);

    this.removeConsumerSubscription(this.currentIssue.issueID, marketName);

    const consumerFromObservable$: any =
      this.sharedSolaceStreamService.getConsumerFromObservable(
        this.currentIssue.issueID,
        marketName,
        this.consumerId,
      );
    const consumers = consumerFromObservable$
      ? consumerFromObservable$.length
      : 0;
    if (consumers === 0) {
      // console.warn('hay que hacer el unsuscribe this.currentIssue.issueID: %o', this.currentIssue.issueID);

      this.sharedSolaceMainService.unSubscribeTopic(
        PROFUNDIDAD,
        this.currentIssue.issueID,
      );
    }
  }

  private subscribeSolace() {
    let PROFUNDIDAD = '';

    if (this.userInfo.hasLevel2) {
      PROFUNDIDAD =
        this.currentIssue.instrumentType === MarketType.BMV
          ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_BMV
          : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD2_SIC;
    } else {
      PROFUNDIDAD =
        this.currentIssue.instrumentType === MarketType.BMV
          ? SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_BMV
          : SOLACE_SUBSCRIPTIONS.PROFUNDIDAD1_SIC;
    }

    this.sharedSolaceMainService.subscribeTopic(
      PROFUNDIDAD,
      this.currentIssue.issueID,
    );
  }

  private solaceSubscriptions() {
    if (this.subscribedSymbol) {
      this.unsubscribeSolace();
    }

    this.subscribeSolace();
    this.subscribedSymbol = true;
  }

  private scanForUserTypeContract() {
    this.userGlobalSubscription = this.store
      .select('userGlobal')
      .subscribe((value: any) => {
        this.isUserGlobal = value;

        this.ordenType = ORDERS_LIST;

        this.handleShortSaleViewership(
          this.userRole === USER_ROLES.ADVISOR && this.isUserGlobal,
        );

        if (this.isUserGlobal) {
          this.hideOrdersWhenGlobal();
        }
      });
  }

  private scanForUserRole() {
    this.userRoleSubscription = this.store
      .select('userRole')
      .subscribe((value: number) => {
        this.userRole = value;
        this.isInternalUser =
          value === USER_ROLES.RI || value === USER_ROLES.SUBDIRECTOR
            ? true
            : false;

        if (this.userRole) {
          this.puppetService.updateCanOperate(this.userRole);
        }

        this.handleShortSaleViewership(
          value === USER_ROLES.ADVISOR && this.isUserGlobal,
        );
      });
  }

  private handleShortSaleViewership(hide: boolean = false) {
    const SHORT_SALE_ID = '11';

    if (hide) {
      this.ordenType = this.ordenType.filter(
        (value: any) => value.id !== SHORT_SALE_ID,
      );
    } else {
      this.ordenType = ORDERS_LIST;
    }
  }

  private hideOrdersWhenGlobal() {
    this.ordenType = this.ordenType.filter(
      (order: any) => order.globalViewable,
    );
  }
}
